import { Fragment, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { REIButton, REIProgressSpinner, REIToastContainer } from "../../components/ui";
import REIToggablePanel from "../../components/ui/toggablePanel";
import ErrorMessage from "../../components/ui/errorMessage";
import { MEMBER_KEY, Messages } from "../../utils/constants";
import { httpDelete, httpPost, useHttpGets } from "../../services/httpService";
import { ApiEndPoints } from "../../utils/endpoints";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

interface IPaymentMethod {
  id: string;
  type: string;
  creditCardHolderName: string;
  creditCardMaskNumber: string;
  creditCardExpirationYear: number;
  creditCardExpirationMonth: number;
  daysToExpiration: number;
  creditCardType: string;
  isDefault: boolean;
  expiresSoon: boolean;
  achAccountName: string;
  achAccountNumberMask: string;
  achAccountType: string;
  achBankName: string;
}

interface IPaymentSummaryData {
  success: boolean;
  creditPaymentMethods: any[];
  achPaymentMethods: any[];
  hasPaymentMethods: boolean;
}

type CardStatus = "active" | "expiring-soon" | "expires-today" | "expired";

const getCardStatus = (expirationYear: number, expirationMonth: number): CardStatus => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiryDate = new Date(expirationYear, expirationMonth, 0);

  if (expiryDate < today) return "expired";
  if (expiryDate.getTime() === today.getTime()) return "expires-today";

  const diffDays = Math.round((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays <= 30 ? "expiring-soon" : "active";
};

const PaymentMethods: React.FC<{
  onsuccess: () => void;
  refreshData: boolean;
  accountId: string;
  cardId: string;
}> = ({ refreshData, accountId, cardId, onsuccess }) => {
  const memberId = localStorage.getItem(MEMBER_KEY);
  const endpoint = `${ApiEndPoints.myaccount}/GetPaymentMethods/?memberId=${memberId}`;
  const [paymentMethodSummary, setPaymentMethodSummary] = useState<IPaymentSummaryData>();
  const [loading, setLoading] = useState<boolean>(false);
  const toastRef = useRef<Toast>(null);
  const [isRefreshData, setIsRefreshData] = useState<boolean>(false);
  const defaultData = useRef<any>();

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    useHttpGets(endpoint, undefined)
      .then((result) => {
        if (result.data && result.status == 200) {
          const creditPaymentMethods = Array.isArray(result.data.creditPaymentMethods)
            ? result.data.creditPaymentMethods
            : [];
          const achPaymentMethods = Array.isArray(result.data.achPaymentMethods) ? result.data.achPaymentMethods : [];

          setPaymentMethodSummary({
            ...result.data,
            creditPaymentMethods,
            achPaymentMethods,
          });
          defaultData.current = creditPaymentMethods.find((pmData) => pmData.isDefault);
          setLoading(false);
        } else {
          defaultData.current = undefined;
          setPaymentMethodSummary(undefined);
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        return <ErrorMessage message={error} />;
      });
  }, [memberId, isRefreshData]);

  useEffect(() => {
    if (refreshData && cardId != null) {
      handleDefaultCardUpdate(cardId);
    }
  }, [cardId && refreshData]);

  const renderHeader = (status: CardStatus) => {
    const config: Record<CardStatus, { bg: string; border: string; icon: string; label: string; textColor: string }> = {
      active: {
        bg: "bg-green-100",
        border: "border-green-300",
        icon: "pi-check-circle",
        label: "Payment Method Active",
        textColor: "text-green-800",
      },
      "expiring-soon": {
        bg: "bg-amber-100",
        border: "border-amber-300",
        icon: "pi-exclamation-circle",
        label: "Payment Method Expiring Soon",
        textColor: "text-amber-800",
      },
      "expires-today": {
        bg: "bg-orange-200",
        border: "border-orange-400",
        icon: "pi-exclamation-triangle",
        label: "Payment Method Expires Today",
        textColor: "text-orange-800",
      },
      expired: {
        bg: "bg-red-200",
        border: "border-red-400",
        icon: "pi-times-circle",
        label: "Payment Method Expired",
        textColor: "text-red-800",
      },
    };
    const { bg, border, icon, label, textColor } = config[status];
      return (
      <div
        className={`${bg} ${border} border-b font-bold rounded-t-lg p-3 text-sm flex items-center gap-2 ${textColor}`}
      >
        <span className={`pi ${icon} text-base`}></span>
        {label}
        </div>
      );
  };

  const renderFooter = (isDefault: boolean, id: string) => {
    return (
      <div className="flex flex-col content-center">
        {isDefault ? (
          <div className="border-solid border-gray-200 border-x border-b border-t-0 rounded-b-lg p-[11px] flex flex-auto gap-3 content-center text-black bg-amber-100">
            <span className="pi pi-star-fill ml-4 content-center text-lg text-amber-400"></span>
            <span className="content-center text-lg"> Default Payment Method</span>
          </div>
        ) : (
          <div className="border-solid border-gray-200 border-x border-b border-t-0 rounded-b-lg  flex flex-auto gap-2">
            <REIButton
              key={`defaultbtn${id}`}
              id={`pmeth-btn-makedefault-${id}`}
              classNames="text-sm bg-transparent text-black m-1"
              label="Make Default"
              onClick={() => {
                updateDefaultPM(id, false);
              }}
            ></REIButton>
            <REIButton
              key={`removebtn${id}`}
              id={`pmeth-btn-remove-${id}`}
              icon="pi pi-check"
              classNames="text-sm m-1"
              label="Remove"
              onClick={() => {
                handleDeletePaymentMethod(id);
              }}
            ></REIButton>
          </div>
        )}
      </div>
    );
  };

  const noPaymentMethodContent = (
    <div className="bg-red-300 rounded-lg" role="alert">
      <div className="p-4 text-red-800">
        <i className="pi pi-exclamation-circle font-bold text-lg"></i>
        <span className="sr-only">Error:</span>
        <p>
          Welcome to the new Real Estate Investar billing platform built on Zuora! As we don't have access to your
          Credit Card number we need you to add your payment method again.{" "}
        </p>
        <p>
          Please do the following as soon as you can;
          <ol>
            <li>Click new payment method</li>
            <li>Enter a valid card number will billing details into the secure form (SSL)</li>
            <li>Once saved we will activate your new billing account.</li>
          </ol>
          You will not be billed for anything immediately but upon your next renewal. If you have any questions, please
          contact our support.
        </p>
      </div>
    </div>
  );

  const removePaymentMethod = async (id: string) => {
    setLoading(true);
    await httpDelete(`${ApiEndPoints.myaccount}/DeletePaymentMethod?paymentId=${id}`)
      .then((data) => {
        if (data.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.DELETE_SUCCESS,
            detail: "Payment Method Deleted Successfully",
            life: 2000,
          });
          setIsRefreshData(!isRefreshData);
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: Messages.DELETE_ERROR,
            detail: data.error,
            life: 2000,
          });
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        toastRef.current?.show({
          severity: "error",
          summary: Messages.DELETE_ERROR,
          detail: error,
          life: 2000,
        });
      });
  };

  const updateDefaultPM = (id: string, isCradAdded: boolean) => {
    setLoading(true);
    httpPost(`${ApiEndPoints.myaccount}/UpdateDefaultPaymentMethod`, null, {
      pmId: `${id}`,
      accountId: `${accountId}`,
    })
      .then((data) => {
        if (data.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Default Payment Method Updated Successfully",
            life: 2000,
          });
          setIsRefreshData(!isRefreshData);
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: Messages.ERROR,
            detail: data.error,
            life: 2000,
          });
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: error,
          life: 2000,
        });
      })
      .finally(() => {
        isCradAdded && onsuccess();
      });
  };

  const handleDeletePaymentMethod = (id: string) => {
    confirmDialog({
      message: <div className="text-lg">Are you sure you want to delete the card?</div>,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => {
        removePaymentMethod(id);
      },
      reject: () => {},
    });
  };

  const handleDefaultCardUpdate = (id: string) => {
        updateDefaultPM(id, true);
  };

  const isDefaultExpired =
    !loading &&
    !!defaultData.current &&
    getCardStatus(defaultData.current.creditCardExpirationYear, defaultData.current.creditCardExpirationMonth) ===
      "expired";

  return (
    <>
      <REIToastContainer toastReference={toastRef}></REIToastContainer>
      <ConfirmDialog />
      {isDefaultExpired && (
        <div
          className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800"
          role="alert"
        >
          <span className="pi pi-exclamation-triangle mt-0.5 text-xl text-red-500 shrink-0"></span>
          <p className="text-sm font-medium leading-5">
            Your default payment method has expired. Please update your payment method to avoid failed payments and
            service interruptions.
          </p>
        </div>
      )}
      {loading && <REIProgressSpinner />}
      {!loading && paymentMethodSummary && !paymentMethodSummary?.hasPaymentMethods && noPaymentMethodContent}
      {!loading && paymentMethodSummary && paymentMethodSummary?.hasPaymentMethods && (
        <>
          <div className="flex flex-wrap h-[260px] overflow-y-scroll">
            {defaultData.current && (
              <div className=" w-1/2 aspect-[1.586/1] p-1 " key={defaultData.current.id}>
                <div className="w-full min-h-[12rem] rounded-lg flex ">
                  <REIToggablePanel
                    customClassName="w-full p-0 payment-panel h-full flex flex-col"
                    istoggleable={false}
                    customHeader={renderHeader(
                      getCardStatus(
                        defaultData.current.creditCardExpirationYear,
                        defaultData.current.creditCardExpirationMonth
                      )
                    )}
                    header=""
                    defaultCollapse={false}
                    footerTemplate={renderFooter(defaultData.current.isDefault, defaultData.current.id)}
                    contentComponent={
                      <>
                        <div className="pl-3">
                          <span className="font-bold align-middle h-5 block">
                            <i className="pi pi-credit-card text-lg"></i> {defaultData.current.creditCardType}
                          </span>
                          <br />
                          <span className="h-5 block">{defaultData.current.creditCardMaskNumber}</span> <br />
                          <span className="h-5 block">
                            Exp : {defaultData.current.creditCardExpirationMonth}/
                            {defaultData.current.creditCardExpirationYear}
                          </span>
                          <br />
                          <span className="h-5 block">{defaultData.current.creditCardHolderName}</span>
                        </div>
                      </>
                    }
                  ></REIToggablePanel>
                </div>
              </div>
            )}
            {paymentMethodSummary?.creditPaymentMethods?.map((creditCardData: IPaymentMethod) => {
              return (
                <Fragment key={creditCardData.id}>
                  {!creditCardData.isDefault && (
                    <div className=" w-1/2 aspect-[1.586/1] p-1">
                      <div className="w-full min-h-[12rem] rounded-lg flex ">
                        <REIToggablePanel
                          customClassName="w-full p-0 payment-panel h-full flex flex-col"
                          istoggleable={false}
                          customHeader={renderHeader(
                            getCardStatus(
                              creditCardData.creditCardExpirationYear,
                              creditCardData.creditCardExpirationMonth
                            )
                          )}
                          header=""
                          defaultCollapse={false}
                          footerTemplate={renderFooter(creditCardData.isDefault, creditCardData.id)}
                          contentComponent={
                            <>
                              <div className="pl-3">
                                <span className="font-bold align-middle h-5 block">
                                  <i className="pi pi-credit-card text-lg"></i> {creditCardData.creditCardType}
                                </span>
                                <br />
                                <span className="h-5 block">{creditCardData.creditCardMaskNumber}</span> <br />
                                <span className="h-5 block">
                                  Exp : {creditCardData.creditCardExpirationMonth}/
                                  {creditCardData.creditCardExpirationYear}
                                </span>
                                <br />
                                <span className="h-5 block">{creditCardData.creditCardHolderName}</span>
                              </div>
                            </>
                          }
                        ></REIToggablePanel>
                      </div>
                    </div>
                  )}
                </Fragment>
              );
            })}
            {paymentMethodSummary?.achPaymentMethods?.map((achData: IPaymentMethod) => {
                return (
                  <div className=" w-1/2 aspect-[1.586/1] p-1 min-h-[12rem]" key={achData.id}>
                    <div className="w-full rounded-lg flex">
                      <REIToggablePanel
                        customClassName="w-full p-0 payment-panel"
                        istoggleable={false}
                      customHeader={renderHeader(
                        getCardStatus(achData.creditCardExpirationYear, achData.creditCardExpirationMonth)
                      )}
                        header=""
                        defaultCollapse={false}
                        footerTemplate={renderFooter(achData.isDefault, achData.id)}
                        contentComponent={
                          <>
                            <div className="pl-3">
                              <span className="font-bold align-middle h-5 block">
                                <i className="pi pi-credit-card text-lg"></i> {achData.creditCardType}
                              </span>
                              <br />
                              <span className="h-5 block">{achData.creditCardMaskNumber}</span> <br />
                              <span className="h-5 block">
                                Exp : {achData.creditCardExpirationYear}/{achData.creditCardExpirationMonth}
                              </span>
                              <br />
                              <span className="h-5 block">{achData.creditCardHolderName}</span>
                            </div>
                          </>
                        }
                      ></REIToggablePanel>
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      )}
    </>
  );
};

export default PaymentMethods;
